import { useCallback, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Stack,
  Group,
  Text,
  Checkbox,
  Radio,
  Alert,
  Progress,
  Divider,
  Box,
  Badge,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconPlayerPause,
  IconPlayerPlay,
} from "@tabler/icons-react";
import {
  useStartStainMarkerVerification,
  useStainMarkerVerificationJob,
  usePauseStainMarkerVerification,
  useResumeStainMarkerVerification,
} from "../../../hooks/admin/useStains";
import {
  StainMarkerVerificationProvider,
  StartMarkerVerificationInput,
} from "../../../services/admin/stains.service";
import StainMarkerVerificationResultsTable from "./StainMarkerVerificationResultsTable";

interface Props {
  opened: boolean;
  onClose: () => void;
}

export default function StainMarkerVerificationModal({
  opened,
  onClose,
}: Props) {
  const [providers, setProviders] = useState<StainMarkerVerificationProvider[]>(
    ["biocare", "leica"]
  );
  const [scope, setScope] =
    useState<StartMarkerVerificationInput["scope"]>("untested");
  const [dryRun, setDryRun] = useState(true);
  const [confirmedLiveRun, setConfirmedLiveRun] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startMutation = useStartStainMarkerVerification();
  const pauseMutation = usePauseStainMarkerVerification();
  const resumeMutation = useResumeStainMarkerVerification();

  const {
    data: jobStatus,
    isLoading: isJobLoading,
    isFetching: isJobFetching,
    isError: isJobError,
  } = useStainMarkerVerificationJob(jobId || undefined);

  const isPaused = useMemo(
    () => jobStatus?.status === "paused",
    [jobStatus?.status]
  );

  const handlePause = useCallback(async () => {
    if (jobId) {
      await pauseMutation.mutateAsync(jobId);
    }
  }, [jobId, pauseMutation]);

  const handleResume = useCallback(async () => {
    if (jobId) {
      await resumeMutation.mutateAsync(jobId);
    }
  }, [jobId, resumeMutation]);

  const handleBiocareToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setProviders((prev) =>
        e.currentTarget.checked
          ? [...prev, "biocare"]
          : prev.filter((p) => p !== "biocare")
      );
    },
    []
  );

  const handleLeicaToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setProviders((prev) =>
        e.currentTarget.checked
          ? [...prev, "leica"]
          : prev.filter((p) => p !== "leica")
      );
    },
    []
  );

  const handleScopeChange = useCallback((val: string) => {
    setScope(val as StartMarkerVerificationInput["scope"]);
  }, []);

  const handleDryRunToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDryRun(e.currentTarget.checked);
    },
    []
  );

  const handleLiveConfirmToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConfirmedLiveRun(e.currentTarget.checked);
    },
    []
  );

  const handleReset = useCallback(() => {
    setJobId(null);
  }, []);

  const handleStart = useCallback(async () => {
    setError(null);

    if (providers.length === 0) {
      setError("Please select at least one provider.");
      return;
    }

    if (!dryRun && !confirmedLiveRun) {
      setError("Please confirm that you want to perform a live run.");
      return;
    }

    try {
      const result = await startMutation.mutateAsync({
        providers,
        scope,
        dryRun,
        confirmedLiveRun: dryRun ? undefined : confirmedLiveRun,
      });

      if (result.total === 0) {
        setError("No stains matched the selected provider and scope.");
        return;
      }

      setJobId(result.jobId);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Failed to start verification.";
      setError(message);
    }
  }, [providers, scope, dryRun, confirmedLiveRun, startMutation]);

  const handleClose = useCallback(() => {
    setJobId(null);
    setConfirmedLiveRun(false);
    setError(null);
    onClose();
  }, [onClose]);

  const progress = useMemo(() => {
    if (!jobStatus || jobStatus.total === 0) return 0;
    const completed =
      (jobStatus.summary.accepted || 0) +
      (jobStatus.summary.rejected || 0) +
      (jobStatus.summary.failed || 0) +
      (jobStatus.summary.timed_out || 0) +
      (jobStatus.summary.dry_run || 0);
    return (completed / jobStatus.total) * 100;
  }, [jobStatus]);

  const isRunning = useMemo(() => {
    if (!jobId) return false;
    if (isJobLoading || isJobFetching) return true;
    if (isJobError || !jobStatus) return false;
    if (jobStatus.status === "paused") return false;
    return jobStatus.status === "running";
  }, [jobStatus, jobId, isJobLoading, isJobFetching, isJobError]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Stain Marker Verification"
      size="xl"
      closeOnClickOutside={!isRunning}
      closeOnEscape={!isRunning}
    >
      <Stack gap="md">
        {!jobId ? (
          <>
            <Text size="sm" c="dimmed">
              Trigger a verification job to ensure stain markers are correctly
              configured for Biocare NeoPATH and Leica BOND stainers.
            </Text>

            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Safety Warning"
              color="orange"
            >
              Live runs will send real test messages to the designated Stainer
              Bridge. Use dry run mode first to verify the scope of the job.
            </Alert>

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Validation Error"
                color="red"
              >
                {error}
              </Alert>
            )}

            <Box>
              <Text fw={500} mb="xs">
                Providers
              </Text>
              <Group>
                <Checkbox
                  label="Biocare NeoPATH"
                  checked={providers.includes("biocare")}
                  onChange={handleBiocareToggle}
                />
                <Checkbox
                  label="Leica BOND"
                  checked={providers.includes("leica")}
                  onChange={handleLeicaToggle}
                />
              </Group>
            </Box>

            <Box>
              <Text fw={500} mb="xs">
                Run Scope
              </Text>
              <Radio.Group value={scope} onChange={handleScopeChange}>
                <Stack gap="xs">
                  <Radio
                    value="untested"
                    label="Untested stains only (Recommended)"
                  />
                  <Radio
                    value="failed_or_untested"
                    label="Failed or untested stains"
                  />
                  <Radio value="all" label="All active stains" />
                </Stack>
              </Radio.Group>
            </Box>

            <Box>
              <Checkbox
                label="Dry Run Mode (Pre-flight check)"
                checked={dryRun}
                onChange={handleDryRunToggle}
              />
              {!dryRun && (
                <Checkbox
                  mt="sm"
                  label="I confirm I want to perform a LIVE run and send messages to stainer bridges."
                  checked={confirmedLiveRun}
                  onChange={handleLiveConfirmToggle}
                  color="red"
                />
              )}
            </Box>

            <Divider />

            <Group justify="flex-end">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                leftSection={<IconPlayerPlay size={16} />}
                onClick={handleStart}
                loading={startMutation.isPending}
                color={dryRun ? "blue" : "red"}
              >
                {dryRun ? "Start Dry Run" : "Start Live Verification"}
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Box>
              <Group justify="space-between" mb="xs">
                <Text fw={500}>
                  Job ID: {jobId}{" "}
                  {jobStatus?.dryRun && (
                    <Badge variant="outline" ml="xs">
                      Dry Run
                    </Badge>
                  )}
                </Text>
                <Text size="sm" c="dimmed">
                  {Math.round(progress)}% Complete
                </Text>
              </Group>
              <Progress
                value={progress}
                animated={isRunning}
                color={isPaused ? "orange" : isRunning ? "blue" : "green"}
              />

              {isPaused && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Job Paused"
                  color="orange"
                  variant="light"
                >
                  Paused. Pending tests will not be sent until you resume.
                  Already-sent tests may still receive responses.
                </Alert>
              )}
            </Box>

            <Group grow>
              <Stack gap={0} align="center">
                <Text size="xl" fw={700}>
                  {jobStatus?.total || 0}
                </Text>
                <Text size="xs" c="dimmed">
                  Total
                </Text>
              </Stack>
              <Stack gap={0} align="center">
                <Text size="xl" fw={700} c="blue">
                  {jobStatus?.summary.pending || 0}
                </Text>
                <Text size="xs" c="dimmed">
                  Pending
                </Text>
              </Stack>
              <Stack gap={0} align="center">
                <Text size="xl" fw={700} c="green">
                  {(jobStatus?.summary.accepted || 0) +
                    (jobStatus?.summary.dry_run || 0)}
                </Text>
                <Text size="xs" c="dimmed">
                  Success
                </Text>
              </Stack>
              <Stack gap={0} align="center">
                <Text size="xl" fw={700} c="red">
                  {(jobStatus?.summary.failed || 0) +
                    (jobStatus?.summary.rejected || 0) +
                    (jobStatus?.summary.timed_out || 0)}
                </Text>
                <Text size="xs" c="dimmed">
                  Failed
                </Text>
              </Stack>
            </Group>

            <Divider />

            <Box>
              <Text fw={500} mb="xs">
                Latest Results
              </Text>
              {isJobLoading && !jobStatus ? (
                <Text size="sm" c="dimmed">
                  Loading results...
                </Text>
              ) : (
                <StainMarkerVerificationResultsTable
                  results={jobStatus?.results || []}
                />
              )}
            </Box>

            <Group justify="flex-end">
              {isRunning && !jobStatus?.dryRun && (
                <Button
                  variant="outline"
                  color="orange"
                  leftSection={<IconPlayerPause size={16} />}
                  onClick={handlePause}
                  loading={pauseMutation.isPending}
                >
                  Pause
                </Button>
              )}

              {isPaused && (
                <Button
                  variant="outline"
                  color="green"
                  leftSection={<IconPlayerPlay size={16} />}
                  onClick={handleResume}
                  loading={resumeMutation.isPending}
                >
                  Resume
                </Button>
              )}

              {!isRunning && !isPaused && (
                <>
                  <Button variant="light" onClick={handleReset}>
                    Run Another
                  </Button>
                  <Button onClick={handleClose}>Done</Button>
                </>
              )}
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
